import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import TableCompetitorPricesComponent from '../components/TableCompetitorPricesComponent';
import CompetitorPriceDetailModal from '../components/CompetitorPriceDetailModal';
import PriceComparisonTabs from '../components/PriceComparisonTabs';
import { validateSession } from '../utils/functions';
import { competitorPricesApi, notifyBadgeChanged, FEATURE_OFF } from '../utils/competitorPricesApi';
import { formatDateTime, statusWarning } from '../utils/competitorSeries';

const FILTERS = [
    ['review', 'Para revisar', 'Con stock nuestro y más caros que el mercado, sin diferencia aceptada.'],
    ['opportunity', 'Oportunidad', 'Más baratos que el mercado, o los únicos con stock.'],
    ['no_stock', 'Sin stock propio', 'No tenemos y otra tienda sí.'],
    ['unconfirmed', 'Edición sin confirmar', 'Con publicaciones que hay que asignar a mano a una edición.'],
    ['all', 'Todos', 'Todos los productos enganchados al comparador.'],
];
const RESULT_LABELS = { ok: 'completa', partial: 'incompleta', error: 'fallida', running: 'en curso' };
const POLL_MS = 5000;

// Precios vs competencia: cómo están parados nuestros precios contra las otras tiendas, según
// el comparador precios.yrvn.dev. La pantalla sólo muestra lo leído: la lectura NO corre desde el
// servidor (el comparador le responde 403 a su IP) sino a pedido desde una máquina del local, así
// que acá no hay botón para leer ni para importar el historial.
//
// Hermana de "Precios vs proveedor": aquélla compara contra lo que nos cobra el distribuidor,
// ésta contra lo que cobran los que venden lo mismo que nosotros.
class CompetitorPricesPage extends Component {

    constructor() {
        super();
        this.state = {
            checked: false,
            role: 0,
            rows: [],
            counts: {},
            status: null,
            thresholdPct: null,
            filter: 'review',
            q: '',
            loaded: false,
            loadError: false,
            featureOff: false,
            open: null,
            reading: false,
            importing: false,
        };
        this._poll = null;
        this._search = null;
    }

    async componentDidMount() {
        const role = await validateSession();
        this.setState({ checked: true, role });
        await this.load();
        // Si al entrar ya hay una lectura o una importación andando, se la sigue.
        const { status } = this.state;
        const reading = Boolean(status && status.lastRun && status.lastRun.status === 'running');
        const importing = Boolean(status && status.lastImport && status.lastImport.status === 'running');
        if (reading || importing) {
            this.setState({ reading, importing });
            this.follow();
        }
    }

    componentWillUnmount() {
        clearTimeout(this._poll);
        clearTimeout(this._search);
    }

    load = (overrides = {}) => {
        const filter = overrides.filter || this.state.filter;
        const q = overrides.q != null ? overrides.q : this.state.q;
        return competitorPricesApi('GET', `?filter=${filter}&q=${encodeURIComponent(q)}`)
            .then((data) => this.setState({
                rows: data.rows || [],
                counts: data.counts || {},
                status: data.status || null,
                thresholdPct: data.thresholdPct,
                loaded: true,
                loadError: false,
                featureOff: false,
            }))
            .catch((error) => {
                if (error && error.code === FEATURE_OFF) {
                    this.setState({ featureOff: true, loaded: true, rows: [] });
                    return;
                }
                // Incluye el 502 de un deploy (res.json() sobre HTML): sin esto la pantalla
                // quedaría con el spinner para siempre.
                this.setState({ loaded: true, loadError: true });
            });
    }

    setFilter = (filter) => {
        this.setState({ filter });
        this.load({ filter });
    }

    onSearch = (event) => {
        const q = event.target.value;
        this.setState({ q });
        clearTimeout(this._search);
        this._search = setTimeout(() => this.load({ q }), 300);
    }

    // Sigue una lectura o una importación en curso consultando el estado, y recarga al terminar.
    follow = () => {
        clearTimeout(this._poll);
        this._poll = setTimeout(() => {
            competitorPricesApi('GET', '/status')
                .then((status) => {
                    const reading = Boolean(status.lastRun && status.lastRun.status === 'running');
                    const importing = Boolean(status.lastImport && status.lastImport.status === 'running');
                    this.setState({ status, reading, importing });
                    if (reading || importing) {
                        this.follow();
                    } else {
                        this.load();
                        notifyBadgeChanged();
                    }
                })
                .catch(() => this.setState({ reading: false, importing: false }));
        }, POLL_MS);
    }

    renderStatus() {
        const { status, reading, importing } = this.state;
        const lastRun = status && status.lastRun;
        const lastImport = status && status.lastImport;
        const warning = statusWarning(status);

        return (
            <div className="mb-3 px-1" style={{ fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <span className="text-muted">
                        Nuestros precios contra las otras tiendas, según el comparador.
                        {lastRun && (
                            <> Última lectura: {formatDateTime(lastRun.startedAt)} ({RESULT_LABELS[lastRun.status] || lastRun.status}
                            {lastRun.status !== 'running' ? `, ${lastRun.changes} ${lastRun.changes === 1 ? 'cambio' : 'cambios'}` : ''}).</>
                        )}
                    </span>
                </div>
                {reading && <div className="alert alert-info mt-2 mb-0">Hay una lectura del comparador en curso. Tarda unos 5 minutos; la lista se actualiza sola al terminar.</div>}
                {importing && (
                    <div className="alert alert-info mt-2 mb-0">
                        Importando el historial{lastImport && lastImport.status === 'running' ? `: ${lastImport.pages} fichas leídas, ${lastImport.changes} cambios traídos` : ''}. Podés seguir trabajando.
                    </div>
                )}
                {!importing && lastImport && lastImport.status !== 'running' && lastImport.failures > 0 && (
                    <div className="alert alert-warning mt-2 mb-0">
                        La última importación del historial dejó {lastImport.failures} {lastImport.failures === 1 ? 'ficha' : 'fichas'} sin traer.
                    </div>
                )}
                {!reading && warning && <div className="alert alert-warning mt-2 mb-0">{warning}</div>}
                {status && status.baseline && lastRun && (
                    <div className="alert alert-secondary mt-2 mb-0">
                        Todavía no terminó la primera lectura completa. Hasta entonces no se registran cambios ni se mandan avisos.
                    </div>
                )}
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }

        const { rows, counts, loaded, filter, featureOff, loadError, open } = this.state;

        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={53} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4" style={{ paddingBottom: '80px' }}>
                            <HeaderComponent title="Comparativa de precios" subtitle="Contra la competencia" />
                            <PriceComparisonTabs active="competitor" />

                            {!loaded && (
                                <div className="containerLoader">
                                    <div className="loader"></div>
                                </div>
                            )}

                            {loaded && featureOff && (
                                <div className="alert alert-secondary">
                                    El panel de precios de la competencia está desactivado. Se activa desde
                                    Configuración &gt; Feature flags.
                                </div>
                            )}

                            {loaded && !featureOff && (
                                <>
                                    {!loadError && this.renderStatus()}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                        <div>
                                            {FILTERS.map(([value, label, title]) => (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    className={`btn btn-sm ${filter === value ? 'btn-primary' : 'btn-white'}`}
                                                    style={{ marginRight: '6px', marginBottom: '4px' }}
                                                    title={title}
                                                    onClick={() => this.setFilter(value)}
                                                >
                                                    {label}{counts[value] != null ? ` (${counts[value]})` : ''}
                                                </button>
                                            ))}
                                        </div>
                                        <input
                                            type="search"
                                            className="form-control form-control-sm"
                                            style={{ maxWidth: '260px' }}
                                            placeholder="Buscar por nombre"
                                            value={this.state.q}
                                            onChange={this.onSearch}
                                        />
                                    </div>
                                    {/* Un error de carga no deja la pantalla trabada: los filtros siguen
                                        ahí y hay un botón para volver a intentar. */}
                                    {loadError && (
                                        <div className="alert alert-warning" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                            <span>No se pudieron cargar los precios.</span>
                                            <button type="button" className="btn btn-sm btn-white" onClick={() => this.load()}>Reintentar</button>
                                        </div>
                                    )}
                                    {!loadError && filter === 'review' && this.state.thresholdPct != null && (
                                        <div className="text-muted px-1 mb-2" style={{ fontSize: '12px' }}>
                                            Productos con stock nuestro y más de {this.state.thresholdPct}% arriba de la mediana del mercado.
                                        </div>
                                    )}
                                    {!loadError && (
                                        <TableCompetitorPricesComponent rows={rows} onOpen={(row) => this.setState({ open: row })} />
                                    )}
                                </>
                            )}
                        </div>
                    </main>
                </div>
                {open && (
                    <CompetitorPriceDetailModal
                        prefix={open.prefix}
                        code={open.code}
                        role={this.state.role}
                        onChange={() => this.load()}
                        onClose={() => { this.setState({ open: null }); this.load(); }}
                    />
                )}
            </div>
        );
    }
}

export default CompetitorPricesPage;
